import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  ATS,
  ATSExtended,
  ATSPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

export enum ATSQueryKeysEnum {
  ATS = 'ats',
  ATSPluralKey = 'atss',
}

// export const useFetchATSsInterval = ({
//   enabled = true,
//   params,
// }: UseFetchEnabledParams<GetATSsParams> = {}) => {
//   return useQuery({
//     queryKey: [
//       ATSQueryKeysEnum.ATSPluralKey,
//       ...Object.values(params ?? {}),
//     ],
//     queryFn: () => getAllATS(params),
//     enabled: enabled,
//     refetchInterval: 60000
//   });
// };

///* tanStack query
export const useFetchATSs = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetATSsParams> = {}) => {
  return useQuery({
    queryKey: [
      ATSQueryKeysEnum.ATSPluralKey,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getAllATS(params),
    enabled: enabled,
  });
};

export const useGetATS = (id: string) => {
  return useQuery({

    queryKey: [ATSQueryKeysEnum.ATS, id?.toString()],
    queryFn: () => getATS(id),
    retry: false,
  });
};

export const useCreateATS = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateATSParams<T>) =>
      createATS(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ATSQueryKeysEnum.ATSPluralKey],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'ATS creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateATS = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,

  keysToInvalidate = [],
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateATSParams<T>) =>
      updateATS(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          ...keysToInvalidate,
          ATSQueryKeysEnum.ATSPluralKey,
        ],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'ATS actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteATS = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteATS,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ATSQueryKeysEnum.ATSPluralKey],
      });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetATSsParams = Partial<ATS> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
  identificacion?: string;
  fecha_instalacion?: string;

  fecha_creacion_after?:string;
  fecha_creacion_before?:string;
  
};
export type CreateATSParamsForm = Omit<
  ATS,
  'id_analisis_trabajo_seguro'
>;
export type CreateATSParams<T> = T;
export type UpdateATSParams<T> = {
  id: string;
  data: T;
};

export const getAllATS = async (params?: GetATSsParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<ATSPagingRes>(
    `${urlBackend}api/v1/analisistrabajoseguro/?${queryParams}`,
    true
  );
};

export const getATS = async (id: string) => {
  try {
    return await get<ATSExtended>(
      `${urlBackend}api/v1/analisistrabajoseguro/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createATS = async <T>(
  data: CreateATSParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ATS>(`${urlBackend}api/v1/analisistrabajoseguro/`, data, true);
};

export const updateATS = async <T>({
  id,
  data,
}: UpdateATSParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ATS>(
    `${urlBackend}api/v1/analisistrabajoseguro/${id}/`,
    data,
    true
  );
};

export const deleteATS = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  return await remove<ATS>(
    `${urlBackend}api/v1/analisistrabajoseguro/${id}/`,
    true
  );
};
